import React, { useContext } from "react";
import { ArrowRight, Building2, Calendar, Minus, X, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ONBOARDING_APPOINTMENT_URL } from "../../services/config";
import AuthContext from "../../hooks/authContext";
import { useOnboardingExperience } from "./OnboardingExperienceProvider";

const PROFILE_PATH = "/account/profile";

export default function OnboardingDecisionModal() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width:767px)");
  const {
    isDecisionModalOpen,
    minimizeDecisionModal,
    dismissForSession,
    openChecklistModal,
    isExperienceReady,
    showOnboardingCallCta,
  } = useOnboardingExperience();
  const isCompanyUser = auth?.userProfile?.role === "companyUser";

  if (!isCompanyUser) {
    return null;
  }

  const isDialogOpen = isDecisionModalOpen && isExperienceReady;

  const handleNavigate = (path) => {
    dismissForSession();
    navigate(path);
  };

  const handleOpenOnboardingAppointment = () => {
    dismissForSession();
    window.open(ONBOARDING_APPOINTMENT_URL, "_blank", "noopener,noreferrer");
  };

  const actions = [
    {
      id: "profile",
      title: "Update Company Profile",
      description: "Review and complete your company details.",
      label: "Update Company Profile",
      icon: Building2,
      onClick: () => handleNavigate(PROFILE_PATH),
    },
    ...(showOnboardingCallCta
      ? [
          {
            id: "schedule",
            title: "Let Our Team Help You Setup ProTeams",
            description: "Book a guided onboarding call with our team.",
            label: "Schedule Onboarding Call",
            icon: Calendar,
            onClick: handleOpenOnboardingAppointment,
          },
        ]
      : []),
    {
      id: "checklist",
      title: "Set Up Your Account Today",
      description: "Walk through the self-serve setup checklist.",
      label: "Set Up Your Account Today",
      icon: ListChecks,
      onClick: openChecklistModal,
    },
  ];

  return (
    <Dialog open={isDialogOpen}>
      <DialogContent
        data-onboarding-decision-modal="true"
        closeButton={false}
        closeOnOutsideClick={!isMobile}
        overlayClassName={isMobile ? "bg-black/30" : undefined}
        className={`flex flex-col overflow-hidden border-0 bg-white p-0 shadow-2xl dark:bg-[#111827] ${
          isMobile
            ? "top-[56%] h-auto max-h-[74dvh] w-[calc(100vw-28px)] max-w-none translate-x-[-50%] translate-y-[-50%] rounded-2xl pb-[env(safe-area-inset-bottom)]"
            : "max-h-[90vh] sm:max-w-[680px]"
        }`}
      >
        <DialogHeader className="space-y-3">
          <div className="border-b border-slate-200 px-5 pb-5 pt-8 dark:border-slate-800 sm:px-6">
            <div className="flex items-start justify-between gap-4">
              <DialogTitle className="text-2xl font-semibold leading-tight text-slate-900 dark:text-white">
                Welcome to ProTeams
              </DialogTitle>
              <div className="flex shrink-0 items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={minimizeDecisionModal}
                  className={`text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white ${
                    isMobile ? "h-9 px-3" : "h-8 w-8 p-0"
                  }`}
                  aria-label="Minimize setup options"
                >
                  <Minus className="h-4 w-4" />
                  {isMobile && <span className="ml-1 text-xs font-medium">Minimize</span>}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={dismissForSession}
                  className={`text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white ${
                    isMobile ? "h-9 px-3" : "h-8 w-8 p-0"
                  }`}
                  aria-label="Close setup options for this session"
                >
                  <X className="h-4 w-4" />
                  {isMobile && <span className="ml-1 text-xs font-medium">Close</span>}
                </Button>
              </div>
            </div>
            <DialogDescription className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Choose how you would like to get started with your account setup.
            </DialogDescription>
          </div>
        </DialogHeader>

        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-5 sm:px-6">
          {actions.map((action) => {
            const Icon = action.icon;

            return (
              <div
                key={action.id}
                className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50/80 px-4 py-4 dark:border-slate-800 dark:bg-slate-900/40 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="grid min-w-0 flex-1 grid-cols-[20px_1fr] items-start gap-x-3 gap-y-1">
                  <Icon
                    size={20}
                    className="mt-0.5 shrink-0 text-slate-400 dark:text-slate-500"
                  />
                  <span className="text-sm font-semibold leading-5 text-slate-900 dark:text-slate-100">
                    {action.title}
                  </span>
                  <span className="col-start-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                    {action.description}
                  </span>
                </div>

                <Button
                  type="button"
                  onClick={action.onClick}
                  className="w-full shrink-0 justify-center bg-[#078037] text-white hover:bg-[#05662a] sm:w-[260px]"
                >
                  {action.label}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
